/**
 * <s-color-cycle> slowly crossfades a product card image through its colour variants.
 * The first (base) image stays in place; extra layers marked [data-color-layer] fade in on top.
 * Pauses when off-screen or the tab is hidden, and never animates for reduced-motion users.
 */
class ColorCycle extends HTMLElement {
  connectedCallback() {
    this.layers = Array.from(this.querySelectorAll('[data-color-layer]'));
    if (!this.layers.length) return;

    this.index = 0;
    this.z = 1;
    this.interval = Number(this.dataset.interval) || 3500;
    this.visible = false;
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.onVisibility = () => this.update();
    this.onMotionChange = () => this.update();
    document.addEventListener('visibilitychange', this.onVisibility);
    this.motionQuery.addEventListener('change', this.onMotionChange);

    this.observer = new IntersectionObserver((entries) => {
      this.visible = entries.some((entry) => entry.isIntersecting);
      this.update();
    });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.stop();
    this.observer?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.motionQuery?.removeEventListener('change', this.onMotionChange);
  }

  update() {
    const canRun = this.visible && !document.hidden && !this.motionQuery.matches;
    if (canRun) {
      this.start();
    } else {
      this.stop();
      if (this.motionQuery.matches) this.show(0);
    }
  }

  start() {
    if (this.timer || this.delayTimer) return;
    const offset = Number(this.dataset.offset) || 0;
    this.delayTimer = setTimeout(() => {
      this.delayTimer = null;
      this.timer = setInterval(() => this.next(), this.interval);
    }, offset);
  }

  stop() {
    clearTimeout(this.delayTimer);
    clearInterval(this.timer);
    this.delayTimer = null;
    this.timer = null;
  }

  next() {
    if (this.hasAttribute('data-random')) {
      this.show(this.randomIndex());
      return;
    }
    this.show((this.index + 1) % (this.layers.length + 1));
  }

  // Random mode: jump to a random layer, preferring a different product than the one showing.
  randomIndex() {
    const count = this.layers.length;
    if (count < 2) return this.index === 1 ? 0 : 1;
    const current = this.index > 0 ? this.layers[this.index - 1] : null;
    const currentProduct = current?.dataset.product;
    let candidates = [];
    for (let i = 1; i <= count; i++) {
      if (i === this.index) continue;
      if (currentProduct && this.layers[i - 1].dataset.product === currentProduct) continue;
      candidates.push(i);
    }
    if (!candidates.length) {
      for (let i = 1; i <= count; i++) if (i !== this.index) candidates.push(i);
    }
    return candidates[Math.floor(Math.random() * candidates.length)];
  }

  show(index) {
    const previous = this.index;
    this.index = index;
    if (index === previous) return;

    if (index === 0) {
      // Fade the top layer away to reveal the base image underneath.
      this.layers.forEach((layer) => layer.removeAttribute('data-active'));
      return;
    }

    const incoming = this.layers[index - 1];
    this.z += 1;
    incoming.style.zIndex = String(this.z);
    incoming.setAttribute('data-active', '');

    // Hide the old layer only once the new one fully covers it, so the base never flashes through.
    const outgoing = previous > 0 ? this.layers[previous - 1] : null;
    if (outgoing) {
      incoming.addEventListener(
        'transitionend',
        () => {
          if (this.index === index) outgoing.removeAttribute('data-active');
        },
        { once: true }
      );
    }
  }
}

if (!customElements.get('s-color-cycle')) {
  customElements.define('s-color-cycle', ColorCycle);
}
